import React, { useEffect, useState, useMemo } from 'react';
import KjedeApi from 'services/KjederegisteretAdminApi/KjedeApi';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import DropdownTreeSelect, { TreeData, TreeNode, TextProps } from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import { KjedeType } from 'services/KjederegisteretAdminApi/KjedeApi/types';
import CommonLoader from 'components/CommonLoader';

export interface KjedeTreNode {
  label: string;
  id: number;
  kjedenr: number;
  name: string;
  enhetId: string;
  nameHierarchy: string[];
  hierarchy: {
    nr: number;
    name: string;
    enhetId: number;
    id: number;
  }[];
  checked?: boolean;
  children?: KjedeTreNode[];
  disabled?: boolean;
  parent?: KjedeTreNode;
}

export interface PickedKjede {
  id: number;
  name: string;
  kjedenr: string;
  enhetId: number;
  hierarchy: PickedKjede[];
}

export enum PickerMode {
  Multi,
  Single,
}

const KjedeTreHelper = {
  mapKjedeTre: (
    kjedeTre: KjedeTreNode[],
    selectedKjedeNr: number[],
    facetCounts?: Map<number, number>,
    hideEmptyFacets?: boolean,
  ): TreeData => {
    const mapped = kjedeTre.map((o) => KjedeTreHelper.mapNode(o, selectedKjedeNr, facetCounts));
    if (!facetCounts || !hideEmptyFacets) return mapped;
    return mapped.filter((node) => KjedeTreHelper.keepNodeWithFacets(node, facetCounts));
  },

  // Skjul noder uten treff (fasett-count 0) med mindre de er valgt eller har et barn som skal beholdes.
  keepNodeWithFacets: (node: KjedeTreNode, facetCounts: Map<number, number>): boolean => {
    node.children = (node.children || []).filter((child) =>
      KjedeTreHelper.keepNodeWithFacets(child, facetCounts),
    );
    const count = facetCounts.get(node.kjedenr) ?? 0;
    return count > 0 || node.checked || node.children.length > 0;
  },

  mapNode: (node: KjedeTreNode, selected: number[], facetCounts?: Map<number, number>) => {
    const isChecked = selected?.includes(node.kjedenr) ? true : false;
    const label = facetCounts
      ? `${node.name} (${(facetCounts.get(node.kjedenr) ?? 0).toLocaleString('nb-NO')})`
      : node.name;

    const item: KjedeTreNode = {
      label,
      name: node.name,
      id: node.id,
      kjedenr: node.kjedenr,
      enhetId: node.enhetId,
      children: [],
      checked: isChecked,
      hierarchy: node.hierarchy || [],
      nameHierarchy: node.nameHierarchy || [],
    };

    if (node.children) {
      node.children.forEach((child: KjedeTreNode) => {
        item.children.push(KjedeTreHelper.mapNode(child, selected, facetCounts));
      });
    }

    return item;
  },
};

interface Props {
  kjedeType?: string;
  pickerMode: PickerMode;
  customTexts?: TextProps;
  disabled?: boolean;
  disableTopNodes?: boolean;
  selected?: number[];
  onSelectionChanged: (kjeder: PickedKjede[]) => void;
  includeFuture?: boolean;
  /** Når satt vises antall treff (fasett) som suffiks på hver node i treet. */
  facetCounts?: Map<number, number>;
  /** Når satt (sammen med facetCounts) skjules uvalgte noder med 0 treff. */
  hideEmptyFacets?: boolean;
}

const KjedePicker = (props: Props) => {
  const isMountedRef = useIsMountedRef();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [kjedeTreData, setKjedeTreData] = useState<KjedeTreNode[]>([]);

  const loadKjeder = async () => {
    setIsLoading(true);
    if (isMountedRef.current) {
      const query = props.includeFuture ? { includeFuture: true } : undefined;
      const res =
        props.kjedeType === KjedeType.Region
          ? await KjedeApi.getRegionTre(query)
          : await KjedeApi.getMarkedsTre(query);
      setKjedeTreData(res);
    }
    setIsLoading(false);
  };

  const handleOnChange = (currentNode: TreeNode, selectedNodes: TreeNode[]) => {
    if (Array.isArray(selectedNodes)) {
      const selectedKjeder = selectedNodes.map((o) => ({
        id: o.id,
        name: o.name ?? o.label,
        kjedenr: o.kjedenr,
        enhetId: o.enhetId,
        hierarchy: o.hierarchy,
      }));
      props.onSelectionChanged(selectedKjeder);
    }
  };

  useEffect(() => {
    if (isMountedRef.current) {
      loadKjeder();
    }
  }, [props.includeFuture]);

  const data = useMemo(() => {
    return KjedeTreHelper.mapKjedeTre(
      kjedeTreData,
      props.selected,
      props.facetCounts,
      props.hideEmptyFacets,
    );
  }, [kjedeTreData, props.selected, props.facetCounts, props.hideEmptyFacets]);

  return (
    <>
      <DropdownTreeSelect
        className="reactDropdownMultiPicker"
        mode={props.pickerMode === PickerMode.Multi ? 'multiSelect' : 'radioSelect'}
        texts={{
          placeholder: 'Velg...',
          noMatches: 'Ingen treff',
        }}
        data={data || []}
        keepTreeOnSearch
        keepChildrenOnSearch
        showPartiallySelected
        onChange={handleOnChange}
        disabled={props.disabled || false}
      />
      {isLoading && (
        <div>
          <CommonLoader />
          <span> Henter data... </span>
        </div>
      )}
    </>
  );
};

export default React.memo(KjedePicker);
